import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Modal from 'components/ui/Modal';
import { DataTablePagination } from 'components/ui/data-table';
import { cn } from 'lib/utils';
import { useTestCategoryLabel } from 'hooks/use-test-category-label';
import * as CollectionsModule from 'modules/collections';
import * as TestModule from 'modules/test';

const PER_PAGE = 20;

interface Props {
  open: boolean;
  onClose: () => void;
  collectionId: string;
  /** Test ids already in the collection (excluded from the list). */
  existingTestIds: string[];
  onAdded: () => void;
}

// Collections also accept `question` items, but this picker only supports
// adding tests today — question support is a separate, deferred scope (the
// candidate list, search, and "already added" exclusion would all need a
// second, question-specific data source).
export const AddItemsModal: React.FC<Props> = ({
  open,
  onClose,
  collectionId,
  existingTestIds,
  onAdded,
}) => {
  const { t } = useTranslation();
  const categoryLabel = useTestCategoryLabel();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);

  // Debounce so typing doesn't re-fire the list request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Reset to page 1 whenever the (debounced) search term changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Was unpaginated (`useList({})` pulled every test in the org in a single
  // request); now paged like the rest of the app (see TestList.tsx).
  const { items: tests = [], meta, isLoading } = TestModule.Hooks.useList({
    params: { search: debouncedSearch || undefined, page, perPage },
    enabled: open,
  });
  const addItem = CollectionsModule.Hooks.useAddItem();

  const existing = useMemo(() => new Set(existingTestIds), [existingTestIds]);

  // Already-in-collection exclusion stays client-side (the backend list
  // endpoint doesn't know about collection membership) — it's per-page, so a
  // page that's entirely already-added tests can render an empty page even
  // though `meta.total` is nonzero; acceptable given the modal's scale.
  const candidates = useMemo(
    () => (tests as Array<{ id: string; title?: string; category?: string }>).filter((item) => !existing.has(item.id)),
    [tests, existing],
  );

  const handleAdd = async (testId: string) => {
    try {
      await addItem.mutateAsync({ collectionId, type: 'test', itemId: testId });
      onAdded();
    } catch {
      /* hook toast */
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={t('collections.add_assessments')}>
      <div className="space-y-3">
        <Input placeholder={t('collections.search_assessments')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="max-h-[50vh] overflow-y-auto divide-y">
          {isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('loading')}</p>
          ) : candidates.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('collections.no_assessments')}</p>
          ) : (
            candidates.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.title || t('collections.untitled')}</p>
                    {categoryLabel(item.category) && (
                      <p className="truncate text-xs text-muted-foreground">{categoryLabel(item.category)}</p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className={cn(addItem.isPending && 'opacity-70')}
                  onClick={() => handleAdd(item.id)}
                  disabled={addItem.isPending}
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> {t('collections.add')}
                </Button>
              </div>
            ))
          )}
        </div>
        {meta.total > 0 && (
          <DataTablePagination
            page={meta.page}
            pageCount={meta.totalPages}
            pageSize={perPage}
            total={meta.total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPerPage(size);
              setPage(1);
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default AddItemsModal;
